import { useState } from "preact/hooks"
import { ChevronDown, ChevronRight, Pencil } from "lucide-react"

import type { GrossToNetBridge, GrossToNetCustomerRow } from "../utils/gross_to_net"

interface GrossToNetPanelProps {
  bridge: GrossToNetBridge
  /** Caption for the window the bridge covers, e.g. "2027 plan · Jan–Dec". */
  caption?: string
  className?: string
  /**
   * When set, the per-customer Rate column becomes editable: committing a new percent
   * rescales that customer's trade spend for the periods on screen (an unsaved edit,
   * persisted by the normal Save).
   */
  on_customer_rate_change?: (customer: string, rate_pct: number) => void
}

function currency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(Number.isFinite(value) ? value : 0)
}

function percent(value: number | null): string {
  return value == null ? "—" : `${value.toFixed(1)}%`
}

/** Trade rates run wide; anything at or above this reads as a number worth a second look. */
const HIGH_TRADE_RATE_PCT = 35

function rate_class(value: number | null): string {
  if (value == null) return "text-gray-400"
  if (value >= HIGH_TRADE_RATE_PCT) return "text-amber-700"
  return "text-gray-800"
}

function BridgeLine(props: { label: string; value: string; muted?: boolean; emphasis?: boolean }) {
  const { label, value, muted, emphasis } = props
  return (
    <div className={`flex items-baseline justify-between gap-4 ${emphasis ? "pt-2" : ""}`}>
      <span className={`text-xs ${emphasis ? "font-semibold text-gray-900" : "text-gray-600"}`}>{label}</span>
      <span
        className={`shrink-0 tabular-nums ${
          emphasis ? "text-lg font-semibold text-gray-900" : `text-sm ${muted ? "text-rose-700" : "text-gray-900"}`
        }`}
      >
        {value}
      </span>
    </div>
  )
}

/** Click-to-edit trade rate: commits on Enter/blur, escapes back to the display value. */
function EditableRateCell(props: {
  row: GrossToNetCustomerRow
  on_commit: (rate_pct: number) => void
}) {
  const { row } = props
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState("")

  function commit(): void {
    setEditing(false)
    const parsed = parseFloat(draft)
    if (!Number.isFinite(parsed) || parsed < 0) return
    if (row.trade_rate_pct != null && Math.abs(parsed - row.trade_rate_pct) < 0.05) return
    props.on_commit(parsed)
  }

  if (!editing) {
    return (
      <button
        type="button"
        className={`group inline-flex items-center gap-1 tabular-nums font-medium ${rate_class(row.trade_rate_pct)}`}
        title={
          row.revenue === 0
            ? "No planned revenue in view — a rate cannot be applied"
            : "Click to set a target trade rate for the periods on screen"
        }
        disabled={row.revenue === 0}
        onClick={() => {
          setDraft(row.trade_rate_pct != null ? row.trade_rate_pct.toFixed(1) : "")
          setEditing(true)
        }}
      >
        {percent(row.trade_rate_pct)}
        <Pencil className="h-3 w-3 text-gray-300 opacity-0 transition-opacity group-hover:opacity-100" />
      </button>
    )
  }

  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="number"
        min="0"
        max="100"
        step="0.1"
        value={draft}
        autoFocus
        onInput={(e) => setDraft((e.target as HTMLInputElement).value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit()
          if (e.key === "Escape") setEditing(false)
        }}
        className="w-16 rounded border border-blue-300 px-1 py-0.5 text-right text-sm tabular-nums focus:outline-none"
      />
      <span className="text-xs text-gray-500">%</span>
    </span>
  )
}

function CustomerTable(props: {
  customers: GrossToNetCustomerRow[]
  on_customer_rate_change?: (customer: string, rate_pct: number) => void
}) {
  const { customers, on_customer_rate_change } = props
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] text-sm">
        <thead>
          <tr className="border-b border-gray-200 text-[11px] uppercase tracking-wide text-gray-500">
            <th className="py-2 pr-3 text-left font-medium">Customer</th>
            <th className="py-2 px-3 text-right font-medium">Revenue</th>
            <th className="py-2 px-3 text-right font-medium">Trade spend</th>
            <th className="py-2 px-3 text-right font-medium">Net sales</th>
            <th className="py-2 pl-3 text-right font-medium">Rate</th>
          </tr>
        </thead>
        <tbody>
          {customers.map((row: GrossToNetCustomerRow) => (
            <tr key={row.customer} className="border-b border-gray-100 last:border-0">
              <td className="py-1.5 pr-3 text-gray-800">{row.customer}</td>
              <td className="py-1.5 px-3 text-right tabular-nums text-gray-800">{currency(row.revenue)}</td>
              <td className="py-1.5 px-3 text-right tabular-nums text-gray-800">{currency(row.spend)}</td>
              <td className="py-1.5 px-3 text-right tabular-nums text-gray-800">{currency(row.net_sales)}</td>
              <td className="py-1.5 pl-3 text-right">
                {on_customer_rate_change ? (
                  <EditableRateCell
                    row={row}
                    on_commit={(rate_pct: number) => on_customer_rate_change(row.customer, rate_pct)}
                  />
                ) : (
                  <span className={`tabular-nums font-medium ${rate_class(row.trade_rate_pct)}`}>
                    {percent(row.trade_rate_pct)}
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Gross-to-net bridge for the periods currently on screen: planned revenue less trade spend,
 * with the spend broken out by who funds it and what it buys.
 *
 * Rendered only when the loaded version carries trade spend on its customer nodes, so versions
 * imported without a spend file are unaffected.
 */
export function GrossToNetPanel(props: GrossToNetPanelProps) {
  const { bridge, caption, className, on_customer_rate_change } = props
  const [expanded, setExpanded] = useState(false)

  return (
    <section className={`rounded-lg border border-gray-200 bg-white shadow-sm ${className || ""}`}>
      <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-100 p-4">
        <div>
          <h2 className="text-xs font-medium uppercase tracking-wide text-gray-500">Gross to net</h2>
          {caption ? <p className="mt-0.5 text-[11px] text-gray-500">{caption}</p> : null}
        </div>
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900"
        >
          {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {expanded ? "Hide" : "Show"} by customer
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 p-4 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <div className="space-y-1.5">
          <BridgeLine label="Gross revenue" value={currency(bridge.revenue)} />
          <BridgeLine label="Retailer spend" value={`−${currency(bridge.retailer_spend)}`} muted />
          <BridgeLine label="Distributor spend" value={`−${currency(bridge.distributor_spend)}`} muted />
          <div className="border-t border-gray-200" />
          <BridgeLine label="Net sales" value={currency(bridge.net_sales)} emphasis />
          <div className="flex items-baseline justify-between gap-4">
            <span className="text-xs text-gray-600">Trade rate</span>
            <span className={`text-sm font-semibold tabular-nums ${rate_class(bridge.trade_rate_pct)}`}>
              {percent(bridge.trade_rate_pct)}
            </span>
          </div>
        </div>

        {bridge.reasons.length > 0 ? (
          <div>
            <div className="mb-2 text-[11px] font-medium uppercase tracking-wide text-gray-500">Spend by reason</div>
            <div className="space-y-1.5">
              {bridge.reasons.map((row) => (
                <div key={row.reason} className="flex items-center gap-3">
                  <span className="w-44 shrink-0 truncate text-xs text-gray-700" title={row.reason}>
                    {row.reason}
                  </span>
                  <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100">
                    <div
                      className="h-full rounded-full bg-blue-500"
                      style={{ width: `${Math.max(row.share_pct, 1)}%` }}
                    />
                  </div>
                  <span className="w-24 shrink-0 text-right text-xs tabular-nums text-gray-800">
                    {currency(row.spend)}
                  </span>
                  <span className="w-12 shrink-0 text-right text-[11px] tabular-nums text-gray-500">
                    {row.share_pct.toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>

      {expanded ? (
        <div className="border-t border-gray-100 p-4 pt-3">
          <CustomerTable customers={bridge.customers} on_customer_rate_change={on_customer_rate_change} />
        </div>
      ) : null}
    </section>
  )
}
